import React, { useEffect, useMemo } from "react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";

export interface PasswordCriteria {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

interface PasswordStrengthMeterProps {
  password: string;
  onStrengthChange?: (strength: number, criteria: PasswordCriteria) => void;
  showCriteria?: boolean;
}

export const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  onStrengthChange,
  showCriteria = true,
}) => {
  // Memoize the criteria calculation to prevent unnecessary recalculations
  const criteria: PasswordCriteria = useMemo(
    () => ({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[^A-Za-z0-9]/.test(password),
    }),
    [password],
  );

  // Calculate the strength based on criteria
  const metCriteria = useMemo(
    () => Object.values(criteria).filter(Boolean).length,
    [criteria],
  );

  const passwordStrength = metCriteria;
  const passwordCriteria = criteria;

  useEffect(() => {
    // Notify parent component about the strength change
    if (onStrengthChange) {
      onStrengthChange(metCriteria, criteria);
    }
  }, [criteria, metCriteria, onStrengthChange]);

  // Get color for password strength meter
  const getStrengthColor = () => {
    if (passwordStrength === 0) return "bg-tertiary";
    if (passwordStrength === 1) return "bg-red-500";
    if (passwordStrength === 2) return "bg-orange-500";
    if (passwordStrength === 3) return "bg-yellow-500";
    if (passwordStrength === 4) return "bg-primary";
    return "bg-primary-600";
  };

  // Get text description of password strength
  const getStrengthText = () => {
    if (!password) return "";
    if (passwordStrength === 0 || passwordStrength === 1) return "Weak";
    if (passwordStrength === 2) return "Fair";
    if (passwordStrength === 3) return "Good";
    if (passwordStrength === 4) return "Strong";
    return "Very Strong";
  };

  // No need to render if no password
  if (!password) return null;

  return (
    <div className="mt-2">
      <div className="flex justify-between items-center">
        <div className="text-xs text-text-light">Password strength:</div>
        <div
          className={`text-xs font-medium ${
            passwordStrength <= 1
              ? "text-red-500"
              : passwordStrength === 2
                ? "text-orange-500"
                : passwordStrength === 3
                  ? "text-yellow-600"
                  : "text-primary-600"
          }`}
        >
          {getStrengthText()}
        </div>
      </div>
      <div className="h-1 w-full bg-tertiary rounded-full mt-1 flex">
        <div
          className={`h-1 rounded-full ${getStrengthColor()}`}
          style={{ width: `${Math.min(100, passwordStrength * 25)}%` }}
        ></div>
      </div>

      {/* Password criteria checklist */}
      {showCriteria && (
        <div className="mt-3 grid grid-cols-1 gap-2">
          <PasswordCriterionItem
            met={passwordCriteria.minLength}
            text="At least 8 characters"
          />
          <PasswordCriterionItem
            met={passwordCriteria.hasUppercase}
            text="At least one uppercase letter (A-Z)"
          />
          <PasswordCriterionItem
            met={passwordCriteria.hasLowercase}
            text="At least one lowercase letter (a-z)"
          />
          <PasswordCriterionItem
            met={passwordCriteria.hasNumber}
            text="At least one number (0-9)"
          />
          <PasswordCriterionItem
            met={passwordCriteria.hasSpecial}
            text="At least one special character (@#$%^&*)"
          />
        </div>
      )}
    </div>
  );
};

// Component to render password criteria item with check/x mark
const PasswordCriterionItem = ({
  met,
  text,
}: {
  met: boolean;
  text: string;
}) => (
  <div className="flex items-center space-x-2">
    {met ? (
      <CheckIcon className="h-4 w-4 text-primary" />
    ) : (
      <XMarkIcon className="h-4 w-4 text-text-light" />
    )}
    <span className={`text-xs ${met ? "text-primary-700" : "text-text-light"}`}>
      {text}
    </span>
  </div>
);

export default PasswordStrengthMeter;
